import type { CalendarMeetingItem } from '@/lib/meetings.ts';
import { addDays, durationMinutes, isSameDay } from '@/lib/week.ts';
import { getLocale } from '@/paraglide/runtime.js';
import { m } from '@/paraglide/messages.js';

/*
 * Every date the agenda shows goes through Intl in the user's own locale, so
 * "15 juni" and "June 15" both fall out of the same code. Never hand-assemble
 * a date string from parts.
 */
function format(date: Date, options: Intl.DateTimeFormatOptions): string {
  return new Intl.DateTimeFormat(getLocale(), options).format(date);
}

export function formatTime(isoTimestamp: string): string {
  return format(new Date(isoTimestamp), { hour: '2-digit', minute: '2-digit' });
}

export function formatDuration(meeting: CalendarMeetingItem): string {
  return m.agenda_minutes({ count: durationMinutes(meeting.starts_at, meeting.ends_at) });
}

/** "10:00 · 30 min" — the week card's one-line meta. */
export function formatTimeAndDuration(meeting: CalendarMeetingItem): string {
  return `${formatTime(meeting.starts_at)} · ${formatDuration(meeting)}`;
}

export function formatWeekdayShort(date: Date): string {
  return format(date, { weekday: 'short' });
}

export function formatWeekdayLong(date: Date): string {
  return format(date, { weekday: 'long' });
}

export function formatDayOfMonth(date: Date): string {
  return format(date, { day: 'numeric' });
}

export function formatDayAndMonth(date: Date): string {
  return format(date, { day: 'numeric', month: 'long' });
}

/** "1 okt, 14:04" — tells apart two moments on the same day. */
export function formatShortDayMonthAndTime(date: Date): string {
  return format(date, {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * "dinsdag 18 augustus" — the date beside a day header that is named rather
 * than dated. "Vandaag" says which day it is only to somebody who already knows
 * what day it is, so today's date carries the weekday the heading gave up.
 */
export function formatWeekdayDayAndMonth(date: Date): string {
  return format(date, { weekday: 'long', day: 'numeric', month: 'long' });
}

/** "Juli 2026" — the header over one month of conversations. */
export function formatMonthAndYear(date: Date): string {
  return format(date, { month: 'long', year: 'numeric' });
}

/**
 * When a conversation is, as somebody scanning a list thinks of it: today and
 * yesterday by name, everything else by its date. A date on today's row reads
 * as older than it is.
 */
export function formatRelativeDay(isoTimestamp: string): string {
  const date = new Date(isoTimestamp);
  const today = new Date();

  if (isSameDay(date, today)) return m.agenda_today();
  if (isSameDay(date, addDays(today, -1))) return m.agenda_yesterday();
  return formatDayAndMonth(date);
}

/** "15 – 21 juni", collapsing the month when both ends share one. */
export function formatWeekRange(start: Date, end: Date): string {
  const sameMonth = start.getMonth() === end.getMonth();
  const from = sameMonth ? formatDayOfMonth(start) : formatDayAndMonth(start);
  return `${from} – ${formatDayAndMonth(end)}`;
}

/* A calendar entry can genuinely have no title; the grid still needs a label. */
export function meetingTitle(meeting: CalendarMeetingItem): string {
  return meeting.title?.trim() || m.agenda_untitled();
}
